import React, { useState, useEffect } from "react";
import {
    Card, CardBody, Button, Spinner, Container, Row, Col, Badge
} from "reactstrap";
import DataTable from "react-data-table-component";
import { useLocation, useNavigate } from "react-router-dom";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import { apiGet } from "../../Utils";
import { ArrowLeft, User, Calendar, Clock, Info, MapPin, Phone } from 'react-feather';
import { H3 } from "../../AbstractElements";

interface BookingDetail {
    id: number;
    patient_name: string;
    patient_mobile: string;
    address: string;
    date: string;
    slot_start_time: string;
    actual_start_at: string | null;
    actual_end_at: string | null;
    bystander_total_amount: string;
    user: {
        name: string;
    };
}

export default function PayoutDetails() {
    const location = useLocation();
    const navigate = useNavigate();

    // Fallback for refresh (though state is lost on refresh if not using localStorage)
    const { bystander_id, bystander_name, status } = (location.state as any) || {};

    const [loading, setLoading] = useState(true);
    const [bookings, setBookings] = useState<BookingDetail[]>([]);

    useEffect(() => {
        if (!bystander_id) {
            navigate(-1);
            return;
        }
        fetchDetails();
    }, [bystander_id, status]);

    const fetchDetails = async () => {
        setLoading(true);
        try {
            const response: any = await apiGet(`admin/payouts/details?bystander_id=${bystander_id}&payout_status=${status}`);
            setBookings(response.data || []);
        } catch (error) {
            console.error("Failed to load payout details", error);
        } finally {
            setLoading(false);
        }
    };

    const totalAmount = bookings.reduce((sum, b) => sum + parseFloat(b.bystander_total_amount), 0);

    const formatTime = (timeStr: string | null) => {
        if (!timeStr) return "N/A";
        try {
            const date = new Date(timeStr);
            if (isNaN(date.getTime())) {
                // Try parsing as H:i:s
                return timeStr.substring(0, 5);
            }
            return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
        } catch (e) {
            return timeStr;
        }
    };

    const columns = [
        {
            name: <span className="text-muted small text-uppercase fw-bold">SL.NO</span>,
            selector: (row: any, index: any) => index + 1,
            width: '80px',
        },
        {
            name: <span className="text-muted small text-uppercase fw-bold">Patient Information</span>,
            cell: (row: BookingDetail) => (
                <div className="py-2">
                    <div className="fw-bold text-dark">{row.patient_name}</div>
                    <div className="text-primary small d-flex align-items-center gap-1">
                        <Phone size={10} /> {row.patient_mobile}
                    </div>
                    {/* <div className="text-muted tiny">Booked by: {row.user?.name}</div> */}
                </div>
            ),
            sortable: true,
        },
        {
            name: <span className="text-muted small text-uppercase fw-bold">Schedule</span>,
            cell: (row: BookingDetail) => (
                <div className="py-2">
                    <div className="d-flex align-items-center gap-2 mb-1">
                        <Calendar size={14} className="text-muted" />
                        <span className="fw-bold text-dark">{new Date(row.date).toLocaleDateString('en-GB')}</span>
                    </div>
                    <div className="d-flex align-items-center gap-2 text-primary small fw-bold">
                        <Clock size={14} />
                        <span>{formatTime(row.actual_start_at)} — {formatTime(row.actual_end_at)}</span>
                    </div>
                </div>
            ),
            sortable: true,
        },
        {
            name: <span className="text-muted small text-uppercase fw-bold">Address</span>,
            cell: (row: BookingDetail) => (
                <div className="py-2">
                    <div className="d-flex gap-1" style={{ color: '#0dcaf0' }}>
                        <MapPin size={14} className="flex-shrink-0 mt-1" />
                        <span className="small fw-medium">{row.address}</span>
                    </div>
                </div>
            ),
        },
        {
            name: <span className="text-muted small text-uppercase fw-bold  w-100">Bystander Share</span>,
            cell: (row: BookingDetail) => (
                <div className=" w-100 fw-bold text-success fs-6">
                    ₹{parseFloat(row.bystander_total_amount).toLocaleString()}
                </div>
            ),
            sortable: true,
        },
    ];

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: '#f8fafc',
                minHeight: '50px',
                borderBottom: '1px solid #e2e8f0',
            },
        },
        headCells: {
            style: { fontSize: '13px', fontWeight: '600' },
        },
        rows: {
            style: { minHeight: '75px' },
        },
    };

    return (
        <div className='page-body' style={{ backgroundColor: '#f4f7f6', minHeight: '100vh' }}>
            <Breadcrumbs pageTitle="Batch Audit" parent="Payouts" title="Execution Details" />

            <Container fluid className="px-4">
                <div className="mb-4">
                    <Button color="light" className="btn-pill shadow-xs border text-dark fw-bold d-flex align-items-center gap-2" onClick={() => navigate(-1)}>
                        <ArrowLeft size={16} /> Back to Ledger
                    </Button>
                </div>

                <Card className="border-0 shadow-lg" style={{ borderRadius: '15px', overflow: 'hidden' }}>
                    <div style={{
                        background: 'linear-gradient(90deg, #064e3b 0%, #10b981 100%)',
                        padding: '2.5rem 2rem',
                        color: 'white'
                    }}>
                        <Row className="align-items-center">
                            <Col md={8}>
                                <div className="d-flex align-items-center gap-4">
                                    <div style={{
                                        width: '70px', height: '70px', borderRadius: '18px',
                                        backgroundColor: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(8px)',
                                        display: 'flex', justifyContent: 'center', alignItems: 'center',
                                        border: '1px solid rgba(255,255,255,0.3)'
                                    }}>
                                        <User size={32} />
                                    </div>
                                    <div>
                                        <h2 className="mb-1 fw-bold">{bystander_name || "Bystander Details"}</h2>
                                        <div className="d-flex align-items-center gap-2">
                                            <span className="opacity-75">Status: </span>
                                            <Badge color={status === 'pending' ? 'warning' : 'success'} pill className="px-2 py-1 text-uppercase" style={{ fontSize: '10px' }}>
                                                {status === 'pending' ? 'Unpaid' : 'Paid'}
                                            </Badge>
                                        </div>
                                    </div>
                                </div>
                            </Col>
                            <Col md={4} className="text-md-end mt-3 mt-md-0">
                                <div className="bg-white p-3 rounded-xl shadow-sm d-inline-block text-start" style={{ minWidth: '220px', borderRadius: '12px' }}>
                                    <div className="text-muted small fw-bold text-uppercase" style={{ letterSpacing: '0.5px' }}>Total Batch Value</div>
                                    <h3 className="text-success mb-0 fw-bold" style={{ fontSize: '1.8rem' }}>₹{totalAmount.toLocaleString()}</h3>
                                    <div className="text-muted small fw-medium">{bookings.length} Shifts Included</div>
                                </div>
                            </Col>
                        </Row>
                    </div>

                    <CardBody className="p-4 bg-white">
                        <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
                            <div className="d-flex align-items-center gap-2 text-dark">
                                <Info size={18} className="text-primary" />
                                <H3 className="mb-0 fw-bold">Audit Stream</H3>
                            </div>
                        </div>

                        <div className="rounded border shadow-sm overflow-hidden">
                            <DataTable
                                columns={columns}
                                data={bookings}
                                progressPending={loading}
                                progressComponent={<div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '40vh' }}><Spinner color="primary" /></div>}
                                highlightOnHover
                                customStyles={customStyles}
                                noDataComponent={<div className="p-5 text-center text-muted">No associated bookings found.</div>}
                            />
                        </div>
                    </CardBody>
                </Card>
            </Container>
            <style>{`
                .btn-pill { border-radius: 50px !important; }
                .rounded-xl { border-radius: 12px !important; }
                .tiny { font-size: 10px; }
            `}</style>
        </div>
    );
}
